import React, {
  useState,
  FC,
  useRef,
  ReactNode,
  isValidElement,
  FocusEvent,
  KeyboardEvent,
  useMemo
} from 'react';
import classNames from 'classnames/bind';

import styles from './index.pcss';
import Icon from '../Icon';

import { FLEEK_STORE } from '../../../constants/constant';

type IconType = React.ReactNode | string;

export type variant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'text'
  | 'icon'
  | 'link'
  | 'danger'
  | 'circular';

export interface BtnProps {
  id?: string | undefined;
  htmlType?: 'button' | 'submit' | 'reset' | undefined;
  variant?: variant;
  size?: 'xs' | 'sm' | 'lg' | 'md' | 'xlg';
  fontWeight?: 'regular' | 'bold';
  /**
   * `xxs`: 8px
   * `xs`: 10px
   * `sm`: 12px
   * `reg`: 14px
   * `md`: 16px
   */
  fontSize?: 'xxs' | 'xs' | 'sm' | 'reg' | 'md';
  btnLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  emphasis?: boolean;
  fullWidth?: boolean;
  ariaLabel?: string;
  href?: string;
  target?: string;
  /** makes circular button translucent -> opacity: 0.7 */
  translucent?: boolean;
  pressed?: boolean;
  diameter?: number;
  radius?: number;
  icon?: IconType;
  iconStyles?: import('../Icon/Icon').IconStyle;
  additionalStyles?: React.CSSProperties
  /** if button controls visibility of other element, pass visible or not */
  ariaExpanded?: boolean;
  /** if button controls visibility of other element, pass element's id */
  ariaControlsId?: string;
  ariaHasPopup?:string;
  customClass?: string;
  storeName?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => void;
  onFocus?: (event: FocusEvent<HTMLButtonElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLAnchorElement>) => void;
  tabIndex?: number;
  applyAriaLabelToButton?: boolean;
  dangerousHTML?: boolean;
  role?: string;
  setColor?: boolean;
  ariaChecked?: boolean;
  hideLabelForA11y?: boolean;
}

const cx = classNames.bind(styles);

const LOADING_TEXT = 'Loading...';

const Button: FC<BtnProps> = (props: BtnProps) => {
  const {
    id,
    btnLabel,
    htmlType = 'button',
    ariaLabel,
    onClick,
    onFocus,
    onBlur,
    onKeyDown, 
    disabled,
    variant = 'primary',
    icon = '',
    size = 'lg',
    loading,
    target,
    href,
    emphasis,
    fullWidth,
    fontWeight = 'bold',
    iconStyles,
    additionalStyles = {},
    translucent,
    diameter = 40,
    ariaExpanded,
    ariaControlsId,
    ariaHasPopup = '',
    pressed,
    fontSize,
    customClass,
    storeName,
    tabIndex,
    applyAriaLabelToButton = true,
    dangerousHTML = false,
    role = '',
    setColor = false,
    hideLabelForA11y
  } = props;

  const [focus, setFocus] = useState<boolean>(false);

  const elementRef = useRef<HTMLButtonElement>(null);

  const handleClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void = event => {
    event.stopPropagation();
    if (onClick) {
      onClick(event);
    }
  };

  const handleFocus: (event: FocusEvent<HTMLButtonElement>) => void = event => {
    event.stopPropagation();
    setFocus(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur: (event: FocusEvent<HTMLButtonElement>) => void = event => {
    event.stopPropagation();
    setFocus(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleKeyDown: (event: KeyboardEvent<HTMLAnchorElement>) => void = event => {
    event.stopPropagation();
    if (onKeyDown) {
      onKeyDown(event);
    } else if (!disabled && focus && ['Enter', ' ', 'Spacebar'].some(key => key === event.key)) {
      elementRef?.current?.click();
    }
  };

  const iconRenderer: (iconEle: IconType) => ReactNode = iconEle => {
    if (iconEle) {
      if (isValidElement(iconEle)) {
        return iconEle;
      }
      const stylesObj: import('../Icon/Icon').IconStyle = iconStyles || {
        color: storeName === FLEEK_STORE ? 'white' : variant === 'primary' ? 'white' : 'black',
        fontWeight
      };
      return <Icon icon={iconEle} styles={{
        ...stylesObj,
        ...additionalStyles
      }} ariaLabel={ariaLabel} />;
    }
    return null;
  };

  const getSideStyle = useMemo(() => {
    return {
      width: diameter,
      height: diameter
    };
  }, [diameter]);

  const isTextBased = variant === 'link' || variant === 'text';

  const classes = cx('btn', customClass, {
    [`${variant}`]: true,
    [`${size}`]: true,
    loading: loading && !disabled,
    disabled: disabled && !isTextBased,
    // blocking usage of regular font weight with non text based buttons
    'font-weight-reg': isTextBased && fontWeight === 'regular',
    'full-width': fullWidth,
    'is-uppercase': emphasis,
    'text-btn': isTextBased,
    'is-disabled': disabled && isTextBased,
    'text-decoration-none': isTextBased,
    'resend-otp-color': (isTextBased && setColor && disabled),
    [`font-${fontSize}`]: !!fontSize && isTextBased
  });

  const circleClass = cx(customClass, {
    'is-translucent': translucent,
    circular: variant === 'circular',
    icon: variant === 'icon',
    [`icon-${size}`]: variant === 'icon',
    'is-active': !!pressed,
    'btn-disabled': disabled
  });

  if (variant === 'link') {
    return (
      <a
        id={id}
        className={classes}
        href={href}
        target={target}
        role={role || 'button'}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label={applyAriaLabelToButton ? ariaLabel : ''}
        onKeyDown={handleKeyDown}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControlsId}
        tabIndex={tabIndex}
      >
        <strong>{btnLabel}</strong>
      </a>
    );
  }

  if (variant === 'circular' || variant === 'icon') {
    return (
      <div
        id={id}
        {...(applyAriaLabelToButton && {'aria-label': `${ariaLabel}`})}
        style={variant === 'circular' ? getSideStyle : undefined}
        role={role || "button"}
        className={circleClass}
        onClick={handleClick}
      >
        {iconRenderer(icon)}
        {btnLabel && variant !== 'icon' ? (
          <span className={cx('circular-btn-text')} {...(hideLabelForA11y ? { 'aria-hidden': 'true' } : {})}>{btnLabel}</span>
        ) : null}
      </div>
    );
  }
  return (
    <button
      id={id}
      className={classes}
      ref={elementRef}
      onClick={handleClick}
      type={htmlType}
      onFocus={handleFocus}
      onBlur={handleBlur}
      aria-label={applyAriaLabelToButton ? ariaLabel : ''}
      onKeyDown={handleKeyDown}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControlsId}
      tabIndex={tabIndex || 0}
      role={role || 'button'}
      {...(typeof props.ariaChecked === 'boolean' ? { 'aria-checked': props.ariaChecked } : {})}
      aria-disabled={disabled ? "true" : "false"}
      aria-haspopup={ariaHasPopup}
    >
      {iconRenderer(icon)}
      {btnLabel ? (
        <span>
          {dangerousHTML ? (
            <strong dangerouslySetInnerHTML={{ __html: loading ? LOADING_TEXT : btnLabel }} />
          ) : (
            <strong>{loading ? LOADING_TEXT : btnLabel}</strong>
          )}
        </span>
      ) : null}
    </button>
  );
};

Button.defaultProps = {
  htmlType: 'button',
  variant: 'primary',
  size: 'lg',
  disabled: false,
  loading: false,
  emphasis: false,
  fullWidth: false,
  fontWeight: 'bold',
  translucent: false,
  diameter: 40,
  customClass: ''
};

export default Button;
